/**
 * Construct and return the full Address component schema
 * @param {Object} properties - The MFA instance properties - from quote_form_schema
 * @returns {Object} - The resolved address container component schema
 */
export const getResolvedConfigSchema = (properties) => {
    // The aim of this schema is provide the input config for the whole address component
    return {
        title: properties.title,
        mfa: {
            stateKey: properties.formStateKey,
            identifier: `${properties.mfaIdentifier}_mfa`,
            backendIdentifier: `${properties.mfaIdentifier}`,
            label: properties.label,
            field: {
                type: 'multiFieldAddress',
                addonWidth: properties.addonWidth ? properties.addonWidth : 'default'
            },
            maskInRecording: true,
            api: {
                ...properties.api,
                onPostcodeChange: {
                    baseUrlName: 'addressServiceBaseUrl',
                    url: 'suburbpostcode',
                    type: 'GET',
                    requestAction: 'GET_SUBURBS_FROM_POSTCODE',
                    responseFuncName: 'setSuburbs',
                    failureType: 'non-fatal'
                }
            },
            rules: [
                ...properties.mfaRules,
                {
                    controls: 'visibility',
                    rule: `unableToFindAddress_${properties.addressType}`
                }
            ],
            renderProps: properties.mfaRenderProps,
            stateOrTerritoryStateKey: properties.stateOrTerritoryOverride || `${properties.mfaIdentifier}.state`,
            elements: [
                { identifier: `${properties.mfaIdentifier}.postcode` },
                { identifier: `${properties.mfaIdentifier}.suburb` },
                { identifier: `${properties.mfaIdentifier}.state` },
                { identifier: `${properties.mfaIdentifier}.streetName` },
                { identifier: `${properties.mfaIdentifier}.streetNumber` },
                { identifier: `${properties.mfaIdentifier}.buildingName` },
                { identifier: `${properties.mfaIdentifier}.unitNumber` },
                { identifier: `${properties.mfaIdentifier}.unitType` }
            ],
            customMFAValidationRules: properties.customMFAValidationRules || {}
        },
        mfaToggle: {
            stateKey: properties.formStateKey,
            type: 'toggle',
            identifier: properties.toggleIdentifier,
            linkText: 'Try searching for your address again ',
            linkIcon: 'ctm_minus',
            linkIconPosition: 'after',
            onClick: {
                actions: [
                    {
                        type: 'RESET_FORM_STATE_VALUES',
                        identifier: properties.mfaIdentifier,
                        stateKey: properties.formStateKey,
                        exceptions: properties.dontReset || []
                    },
                    {
                        type: 'SET_VALUE',
                        identifier: `helpers.unableToFindAddress_${properties.addressType}`,
                        value: 'N',
                        stateKey: properties.formStateKey
                    }
                ]
            },
            rules: [
                {
                    controls: 'visibility',
                    rule: `unableToFindAddress_${properties.addressType}`
                }
            ]
        },
        autoComplete: {
            identifier: properties.autoCompleteIdentifier,
            label: properties.label,
            stateKey: properties.formStateKey,
            field: {
                type: 'autoCompleteCombo',
                placeholder: 'e.g. 5/20 Sample Street',
                debounceTime: 500,
                addonWidth: 'default',
                autoFocus: properties.autoFocus ? properties.autoFocus : false,
                noResultsMessage: 'Sorry, we couldn\'t find your address. Try entering it manually',
                dontReset: properties.dontReset || []
            },
            config: {
                minChars: properties.minChars ? properties.minChars : 2,
                minCharsType: properties.minCharsType ? properties.minCharsType : 'alpha',
                labelTemplate: '{text}',
                valueTemplate: '{text}',
                helpers: {
                    triggerManualEntry: `helpers.unableToFindAddress_${properties.addressType}`
                },
                stateKeyMap: {
                    postcode: 'postCode',
                    suburb: 'suburbName',
                    streetNumber: 'houseNoSel',
                    buildingName: 'buildingName',
                    unitNumber: 'unitSel',
                    gnafId: 'gnafId',
                    streetName: 'streetName',
                    state: 'state',
                    unitType: 'unitType'
                },
                targetIdentifier: `${properties.mfaIdentifier}`
            },
            maskInRecording: true,
            api: {
                ...properties.api,
                onChange: {
                    baseUrlName: 'addressServiceBaseUrl',
                    ...properties.autoCompleteUrl,
                    type: 'POST',
                    requestPayloadValueName: 'addressLine',
                    requestAction: 'POST_PAYLOAD_ADDRESS',
                    failureType: 'non-fatal'
                }
            },
            rules: [
                ...properties.autoCompleteRules,
                {
                    controls: 'visibility',
                    rule: `!unableToFindAddress_${properties.addressType}`
                }
            ]
        },
        autoCompleteToggle: {
            type: 'toggle',
            identifier: properties.toggleIdentifier,
            label: 'Can\'t find your address?',
            linkText: 'Enter it manually ',
            linkIcon: 'ctm_plus',
            linkIconPosition: 'after',
            onClick: {
                actions: [
                    {
                        type: 'RESET_FORM_STATE_VALUES',
                        identifier: properties.mfaIdentifier,
                        stateKey: properties.formStateKey,
                        exceptions: properties.dontReset || []
                    },
                    {
                        type: 'RESET_VALUES',
                        identifiers: [
                            properties.autoCompleteIdentifier
                        ],
                        stateKey: properties.formStateKey
                    },
                    {
                        type: 'SET_VALUE',
                        identifier: `helpers.unableToFindAddress_${properties.addressType}`,
                        value: 'Y',
                        stateKey: properties.formStateKey
                    }
                ]
            },
            rules: [
                {
                    controls: 'visibility',
                    rule: `!unableToFindAddress_${properties.addressType}`
                }
            ]
        }
    };
};

/**
 * Return the stateKeys for the address subcomponents for FormSchemaModel
 * @param {Object} elementSchema - The base MFA schema from quote_form_schema
 * @returns {Array} - The array of address component stateKeys
 */
export const getAddressStateKeys = (elementSchema) => {
    const resolvedSchema = getResolvedConfigSchema(elementSchema.properties);
    return [resolvedSchema.autoComplete, resolvedSchema.mfa];
};
